// ─────────────────────────────────────────────
//  captureDay — read ONE facility's ONE day from live Firestore and write it
//  into the local plane a testing session will boot into.
//
//  ⚠️ THIS RUNS BEFORE ENTERING, AND IT HAS TO. `firebase/config.js` swaps `db`
//  to the local store at MODULE SCOPE when the testing flag is set, so once a
//  session has entered, the app can no longer read live Firestore and cannot
//  snapshot anything. The pre-entry screen is therefore not a UX nicety — it is
//  the only moment both databases are reachable.
//
//  ⚠️ TWO DATABASES, TWO SETS OF PRIMITIVES. Reads go through firebase/sdk
//  (whichever impl this boot selected — real, here). Writes go through
//  sandbox/localDb's OWN primitives, because `assertDb` rejects any instance
//  that is not a local store: "real and local data must never mix". Importing
//  one set for both would either read the empty local store or throw.
//
//  ⚠️ IT REFUSES TO RUN INSIDE A TESTING SESSION. Capturing while already in
//  test mode would snapshot the snapshot, quietly freezing whatever edits the
//  tester had made into a new "real" day.
// ─────────────────────────────────────────────
import { buildManifest, SNAPSHOT_PLAN } from "../../domain/testSnapshot";

/**
 * @param facilityId   the facility being captured
 * @param date         the FACILITY's day key — dayKey(new Date(), facilityTz(f)),
 *                     never rosterDateKey (two definitions still coexist, and
 *                     the device-local one captures a different day than the
 *                     one the user picked)
 * @param rosterUids   the accounts on that day's roster; the ONLY `users` docs
 *                     captured
 * @param readCollection  async (collection, {facilityId, date, ids, scope}) => docs[]
 * @param writeDocs       async (collection, docs) => void   (into the local store)
 * @param alreadyInTestMode  guard; the caller passes isTestModeBoot()
 */
export async function captureDay({
  facilityId,
  date,
  rosterUids = [],
  readCollection,
  writeDocs,
  alreadyInTestMode = false,
  plan = SNAPSHOT_PLAN,
}) {
  if (alreadyInTestMode) {
    throw new Error(
      "[testing] refusing to capture from inside a testing session — that would " +
      "snapshot the snapshot."
    );
  }
  if (!facilityId || !date) {
    throw new Error("[testing] a capture needs both a facility and a day.");
  }
  if (typeof readCollection !== "function" || typeof writeDocs !== "function") {
    throw new Error("[testing] capture needs both a reader and a writer.");
  }

  const results = [];
  for (const step of plan) {
    const { collection, scope, dateField } = step;
    try {
      // `users` is captured by id, and an empty roster means capture NOTHING —
      // not "everyone". A missing roster must never widen the read.
      if (scope === "roster" && rosterUids.length === 0) {
        results.push({ collection, captured: true, count: 0 });
        continue;
      }
      const docs = await readCollection(collection, {
        facilityId,
        date,
        scope,
        // Which field carries the day for THIS collection — not always `date`.
        dateField: dateField || "date",
        ids: scope === "roster" ? rosterUids : null,
      });
      const list = Array.isArray(docs) ? docs : [];
      await writeDocs(collection, list);
      results.push({ collection, captured: true, count: list.length });
    } catch (err) {
      // ⚠️ RECORDED, NEVER SWALLOWED. A collection that could not be read is
      // precisely the one a tester must not mistake for an empty day, so the
      // failure travels into the manifest and onto the screen. One collection
      // failing does not abort the rest — a partial snapshot that KNOWS it is
      // partial is more useful than none.
      console.error(`[testing] capture failed for ${collection}:`, err);
      results.push({
        collection,
        captured: false,
        count: 0,
        error: err?.code || err?.message || "unknown",
      });
    }
  }

  return buildManifest(results);
}

export default captureDay;
